import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { reflectionsAPI } from '../../api/client';
import { ReflectionDetail } from '../ReflectionDetail';
import { ReflectionList } from '../ReflectionList';
import type { ReflectionDetail as ReflectionDetailType, ReflectionSummary } from '../../types';
import { DataSharingNudge } from '../DataSharingNudge';

export function ReflectionsScreen() {
  const [searchParams] = useSearchParams();
  const deepLinkWeek = searchParams.get('week');

  const [items, setItems] = useState<ReflectionSummary[]>([]);
  const [selectedWeek, setSelectedWeek] = useState<string | null>(deepLinkWeek);
  const [detail, setDetail] = useState<ReflectionDetailType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    reflectionsAPI
      .list()
      .then((rows) => {
        setItems(rows);
        if (!selectedWeek && rows[0]) {
          setSelectedWeek(rows[0].week);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedWeek) return;
    reflectionsAPI.detail(selectedWeek).then(setDetail).catch(() => setDetail(null));
  }, [selectedWeek]);

  const currentDelta = useMemo(() => {
    if (!detail) return null;
    const current = Number((detail.metadata.current_period as Record<string, unknown> | undefined)?.total || 0);
    const previous = Number((detail.metadata.previous_period as Record<string, unknown> | undefined)?.total || 0);
    return current - previous;
  }, [detail]);

  return (
    <div className="h-full overflow-hidden max-w-2xl mx-auto px-4 pt-4 pb-20 flex flex-col">
      <header className="mb-3">
        <h1 className="text-xl font-bold text-ink" data-ph-no-mask>Weekly Reflections</h1>
        <p className="text-sm text-ink-secondary" data-ph-no-mask>Wise-friend coaching with current vs previous period insights.</p>
      </header>

      {detail && <DataSharingNudge />}

      {currentDelta !== null && (
        <div className="mb-3 p-3 rounded-xl bg-canvas border border-edge">
          <p className="text-xs text-ink-tertiary" data-ph-no-mask>Current vs previous period</p>
          <p className={`text-sm font-semibold ${currentDelta >= 0 ? 'text-success' : 'text-warning'}`}>
            {currentDelta >= 0 ? '+' : ''}
            {currentDelta} message delta
          </p>
        </div>
      )}

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-ink-tertiary" data-ph-no-mask>Loading reflections...</div>
      ) : (
        <div className="grid md:grid-cols-[300px,1fr] gap-3 h-[calc(100dvh-12rem)]">
          <div className="overflow-y-auto glass-scroll pr-1">
            <ReflectionList reflections={items} selectedWeek={selectedWeek} onSelect={setSelectedWeek} />
          </div>
          <div className="min-h-0">
            <ReflectionDetail reflection={detail} />
          </div>
        </div>
      )}
    </div>
  );
}

export default ReflectionsScreen;
